import { useEffect, useState } from 'react';
import { NotebookPen } from 'lucide-react';
import { useObservation } from '@/lib/state/useObservation';

/**
 * Per-item free-text notes — surfaced under the indicator grid so observers
 * can capture context ("water play only observed briefly", "child refused
 * snack"). Debounced into `itemNotes[itemId]` on the observation record;
 * autosave picks it up via the provider.
 */
export function ItemNotes({ itemId }: { itemId: string }) {
  const { state, setItemNote } = useObservation();
  const persisted = state.itemNotes[itemId] ?? '';
  const [draft, setDraft] = useState(persisted);

  // Keep local draft in sync when switching items.
  useEffect(() => {
    setDraft(persisted);
  }, [itemId, persisted]);

  // Debounce typing → global state (which is itself debounced → Dexie).
  useEffect(() => {
    if (draft === persisted) return;
    const handle = setTimeout(() => setItemNote(itemId, draft), 200);
    return () => clearTimeout(handle);
  }, [draft, persisted, itemId, setItemNote]);

  return (
    <details className="border-t bg-muted/30">
      <summary className="flex cursor-pointer items-center gap-2 px-6 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        <NotebookPen className="h-3.5 w-3.5" aria-hidden />
        Observer notes {persisted ? `· ${persisted.length} chars` : ''}
      </summary>
      <div className="px-6 pb-3">
        <textarea
          rows={3}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Context for this item — things you observed, caveats, follow-ups. Saved automatically."
          aria-label="Observer notes for this item"
          className="w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm leading-relaxed outline-none focus:border-ring focus:ring-2 focus:ring-ring/20"
        />
      </div>
    </details>
  );
}
